import { useMutation, useQuery } from "@apollo/client/react";
import { useAuth, usePremiumFeature, useTemplates } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useCallback, useRef, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import {
  SOAP_FIELDS,
  TEMPLATE_TYPES,
  TRANSCRIPT_SAMPLE_MAPPINGS,
} from "./constants";
import TemplatesFreePlanState from "./empty-states/TemplatesFreePlanState";
import TemplateBuilder from "./TemplateBuilder";
import {
  processField,
  isTemplateSaveable,
} from "./TemplateBuilder/utils";
import { createNewTemplate } from "./utils";
import {
  UPDATE_AUTO_TEMPLATE,
  DELETE_AUTO_TEMPLATE,
  CREATE_AUTO_TEMPLATE,
} from "../../graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATE_BY_UUID } from "../../graphql/queries/AutoTemplates";
import { useRecordingJobType } from "../../hooks";
import { useOnboardingMilestones } from "../../hooks/use-onboarding-milestones";
import { alert } from "../common/Alert";
import FourohFour from "../home/FourohFour";

function resetFormDataFromQuery(
  data,
  reset,
  templateVariant,
  { forceBlankName = false } = {},
) {
  if (data?.autoTemplateById) {
    const template = data?.autoTemplateById;
    const templateFields = template.fields?.map((field) =>
      processField(
        field.fieldName || "",
        field.fieldValue || "",
        field.fieldIdx || 0,
      ),
    );
    const sortedTemplateFields = templateFields.sort(
      (a, b) => a.fieldIdx - b.fieldIdx,
    );
    reset({
      uuid: template.uuid,
      name: forceBlankName ? "" : (template.name ?? ""),
      description: template.description ?? "",
      templateType: template.templateType ?? TEMPLATE_TYPES.SOAP,
      isFavourite: template.isFavourite ?? false,
      userPreferences: template.userPreferences ?? "",
      pdfStorageObjectName: template.pdfStorageObjectName ?? "",
      templateFields: sortedTemplateFields,
      sampleTranscript:
        TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      templateVariant: templateVariant ?? "DEFAULT",
      sharingType: template.sharingType,
      sharedEmails: template.sharedEmails ?? [],
      teamDefaults: template.teamDefaults ?? [],
      dentalChart: template.dentalChart ?? false,
      user: template.user,
    });
  }
}

const AutoTemplateForm = () => {
  const [templateConstructionChoice, setTemplateConstructionChoice] =
    useState("scratch");
  const [builderView, setBuilderView] = useState(null);
  const navigate = useNavigate();
  const { uuid: routeUuid } = useParams();
  const isUncreated = !routeUuid;
  const [createdUuid, setCreatedUuid] = useState(null);
  const uuid = isUncreated ? createdUuid : routeUuid;

  const { userUuid } = useAuth();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const { refetchDefaultTemplates, refetchTemplates } =
    useTemplates();
  const { completeMilestone } = useOnboardingMilestones();
  const {
    selectedJobType,
    setSelectedJobType,
    resetSelectedJobType,
  } = useRecordingJobType();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = isUncreated || searchParams.get("isNew") === "true";
  const isQuillCreated = searchParams.get("source") === "quill";
  const formRef = useRef(null);
  const lastLoadedUuidRef = useRef(null);
  const [previewContent, setPreviewContent] = useState("");
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Quill creates and saves the template server-side, so the user may
  // never hit save themselves. Mark the milestone as soon as we land.
  const milestoneMarkedRef = useRef(false);
  useEffect(() => {
    if (isQuillCreated && !milestoneMarkedRef.current) {
      milestoneMarkedRef.current = true;
      completeMilestone("template_created");
    }
  }, [isQuillCreated, completeMilestone]);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] =
    useState(false);

  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [deleteAutoTemplate] = useMutation(DELETE_AUTO_TEMPLATE);
  const [createAutoTemplateMutation] = useMutation(
    CREATE_AUTO_TEMPLATE,
  );

  const methods = useForm({
    defaultValues: {
      uuid: "",
      name: "",
      description: "",
      templateType: TEMPLATE_TYPES.SOAP,
      isFavourite: false,
      userPreferences: "",
      pdfStorageObjectName: "",
      templateFields: SOAP_FIELDS,
      sampleTranscript:
        TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
      templateVariant: "DEFAULT",
      sharingType: "PRIVATE",
      sharedEmails: [],
      teamDefaults: [],
      dentalChart: false,
    },
    mode: "onSubmit",
  });

  const { control, reset } = methods;

  const {
    data: templateData,
    refetch,
    loading,
    error,
  } = useQuery(GET_AUTO_TEMPLATE_BY_UUID, {
    variables: { uuid },
    skip: !uuid || isUncreated,
    fetchPolicy: "network-only",
  });

  const templateOwnerUuid =
    templateData?.autoTemplateById?.user?.uuid || null;

  const doesCurrentUserOwnThisTemplate =
    isNew ||
    isUncreated ||
    !templateOwnerUuid ||
    templateOwnerUuid === userUuid;

  useEffect(() => {
    if (!templateData) {
      return;
    }
    const template = templateData?.autoTemplateById;
    if (!template?.uuid) {
      return;
    }
    if (lastLoadedUuidRef.current === template.uuid) {
      return;
    }
    lastLoadedUuidRef.current = template.uuid;
    resetFormDataFromQuery(templateData, reset, "DEFAULT", {
      forceBlankName: isNew,
    });
    mixpanel.track("Template builder opened", {
      templateUuid: uuid,
      isNew,
      templateType: template?.templateType ?? TEMPLATE_TYPES.SOAP,
    });
  }, [templateData, reset, uuid, isNew]);

  const refetchAndUpdateSharingOnly = async () => {
    const result = await refetch();
    if (result?.data) {
      const currentFormData = methods.getValues();
      const template = result.data?.autoTemplateById;

      reset({
        ...currentFormData,
        sharingType: template?.sharingType,
        sharedEmails: template?.sharedEmails ?? [],
        teamDefaults: template?.teamDefaults ?? [],
      });
    }
    // Refresh default templates
    refetchDefaultTemplates();
    return result;
  };

  const handleCreateTemplate = useCallback(async () => {
    const formData = methods.getValues();
    const newUuid = await createNewTemplate(
      formData.templateType || TEMPLATE_TYPES.SOAP,
      true,
      createAutoTemplateMutation,
    );
    if (newUuid) {
      setCreatedUuid(newUuid);
      methods.setValue("uuid", newUuid);
      methods.setValue("isFavourite", true);
      // Update URL without re-mounting the component
      window.history.replaceState(
        null,
        "",
        `/templates/edit/${newUuid}?isNew=true`,
      );
    }
    return newUuid;
  }, [methods, createAutoTemplateMutation]);

  const onSubmit = async (formData) => {
    const validation = isTemplateSaveable(formData);
    if (!validation.isValid) {
      alert("warning", validation.message);
      return;
    }

    // If the template is the currently selected jobType, update the name on the dropdown
    if (selectedJobType?.value === uuid) {
      setSelectedJobType({
        ...selectedJobType,
        name: formData.name,
      });
    }
    try {
      // If the template hasn't been created yet (e.g. Quill flow), create it now
      let saveUuid = uuid;
      if (!saveUuid) {
        saveUuid = await handleCreateTemplate();
        if (!saveUuid) {
          alert(
            "error",
            "Error creating template. Please try again.",
          );
          return;
        }
      }

      const validTemplateFields =
        formData.templateFields?.filter((field) => {
          const { fieldName, fieldValue, fieldIdx } = field || {};
          return (
            fieldName &&
            fieldValue !== undefined &&
            fieldValue !== null &&
            fieldIdx !== undefined
          );
        }) || [];

      await updateAutoTemplate({
        variables: {
          input: {
            templateUuid: saveUuid,
            name: formData.name,
            description: formData.description,
            userPreferences: formData.userPreferences,
            templateType: formData.templateType,
            pdfStorageObjectName: formData.pdfStorageObjectName,
            templateFields: validTemplateFields,
            isFavourite: formData.isFavourite,
            dentalChart: formData.dentalChart ?? false,
          },
        },
      });

      await refetch({
        variables: {
          uuid: saveUuid,
        },
      });
      refetchTemplates();

      refetchDefaultTemplates();

      reset(formData, { keepValues: true });

      // Navigate to the real URL and clear isNew flag
      if (isNew || isUncreated) {
        navigate(`/templates/edit/${saveUuid}`, { replace: true });
      }

      if (isNew || isQuillCreated) {
        completeMilestone("template_created");
      }
      const templateLogString = isNew ? "created" : "updated";
      mixpanel.track(`Template ${templateLogString}`, {
        templateUuid: saveUuid,
        templateName: formData.name,
        templateType: formData.templateType,
        isUserPreferencesFilled: !!formData.userPreferences,
        isPreviewFilled: !!previewContent,
        templateConstructionChoice,
        builderView,
        dentalChart: !!formData.dentalChart,
      });
      setShowSaveSuccess(true);
      alert(
        "success",
        <h5 className="text-sm font-normal text-white">
          Template {templateLogString} successfully. To use this
          template,{" "}
          <a
            onClick={() => {
              navigate("/dashboard");
            }}
            className="underline cursor-pointer hover:text-indigo-600"
          >
            click here
          </a>{" "}
          and select it when creating a recording.
        </h5>,
      );
    } catch (error) {
      if (
        error.message ===
        "Template with the same name already exists."
      ) {
        alert(
          "error",
          "Please rename this template. You already have a template with this name.",
        );
        mixpanel.track("Template name conflict", {
          templateName: formData.name,
        });
      } else {
        alert("error", "Error updating template. Please try again.");
        mixpanel.track("Template update error", {
          templateType: formData.templateType,
          error: error.message,
        });
      }
    }
  };

  const handleDelete = async (showToast = true) => {
    if (!uuid) {
      // Template was never created — just navigate away
      navigate("/templates");
      return;
    }
    const formData = methods.getValues();
    try {
      await deleteAutoTemplate({
        variables: { templateUuid: uuid },
      });
      mixpanel.track("Template deleted", {
        templateUuid: uuid,
        templateName: formData.name,
        templateType: formData.templateType,
      });
      if (selectedJobType?.value === uuid) {
        resetSelectedJobType();
      }
      refetchTemplates();
      refetchDefaultTemplates();
      navigate("/templates");
      if (showToast) {
        alert("success", "Template deleted successfully.");
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("error", "Error deleting template. Please try again.");
    }
  };

  if (!hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED)) {
    return <TemplatesFreePlanState />;
  }

  if (error && !isUncreated) {
    alert("error", error.message);
    navigate("/templates");
  }

  // Only allow the template owner to view the edit page
  if (!doesCurrentUserOwnThisTemplate) {
    return <FourohFour />;
  }

  return (
    <>
      {(isUncreated || !loading) && (
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            ref={formRef}
          >
            <TemplateBuilder
              control={control}
              title={isNew ? "Create Template" : "Edit Template"}
              isNew={isNew}
              handleDelete={handleDelete}
              shouldShowDeleteModal={shouldShowDeleteModal}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              refetchSharingOnly={refetchAndUpdateSharingOnly}
              templateConstructionChoice={templateConstructionChoice}
              setTemplateConstructionChoice={
                setTemplateConstructionChoice
              }
              previewContent={previewContent}
              setPreviewContent={setPreviewContent}
              doesCurrentUserOwnThisTemplate={
                doesCurrentUserOwnThisTemplate
              }
              onBackClick={() => navigate("/templates")}
              formRef={formRef}
              showSaveSuccess={showSaveSuccess || isQuillCreated}
              onDismissSaveSuccess={() => {
                setShowSaveSuccess(false);
                if (isQuillCreated) {
                  setSearchParams(
                    (prev) => {
                      prev.delete("source");
                      return prev;
                    },
                    { replace: true },
                  );
                }
              }}
              onBuilderViewChange={setBuilderView}
              onCreateTemplate={handleCreateTemplate}
              isUncreated={isUncreated}
              isQuillCreated={isQuillCreated}
            />
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default AutoTemplateForm;
